/**
 * CmdKPalette — Global ⌘K / Ctrl+K palette to jump to any lead, contact
 * or page by typing a name, email, phone, or company (P2 feature, May
 * 2026). Lives inside MainLayout so it's available on every authenticated
 * page.
 *
 * Search behavior:
 *  - Debounced 200ms search hitting GET /api/leads with `?search=` to
 *    leverage existing server-side indexing.
 *  - Also searches contacts via GET /api/contacts?search=.
 *  - Static navigation entries are always shown first (filtered by query).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Search, Loader2, ArrowRight, LayoutDashboard, Users, Target, MessageSquare, FileText, Calendar, Phone, Bot, FolderOpen, BarChart3, Settings, Mail } from "lucide-react";

const NAV_ENTRIES = [
  { id: "/dashboard", label: "Dashboard", icon: LayoutDashboard, keys: ["dashboard", "home"] },
  { id: "/leads", label: "Leads", icon: Target, keys: ["leads", "pipeline"] },
  { id: "/customers", label: "Customers", icon: Users, keys: ["customers", "contacts"] },
  { id: "/communications", label: "Communications", icon: MessageSquare, keys: ["communications", "email", "comms"] },
  { id: "/quotes", label: "Quotes & Contracts", icon: FileText, keys: ["quotes", "contracts"] },
  { id: "/calendar", label: "Calendar", icon: Calendar, keys: ["calendar"] },
  { id: "/dialer", label: "Sales Dialer", icon: Phone, keys: ["dialer", "calls"] },
  { id: "/templates", label: "Templates", icon: Mail, keys: ["templates"] },
  { id: "/ai-agents", label: "AI Agents", icon: Bot, keys: ["ai", "agents"] },
  { id: "/resources", label: "Resources", icon: FolderOpen, keys: ["resources"] },
  { id: "/reports", label: "Reports", icon: BarChart3, keys: ["reports", "analytics"] },
  { id: "/settings", label: "Settings", icon: Settings, keys: ["settings"] },
];

export default function CmdKPalette() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ leads: [], contacts: [] });
  const [busy, setBusy] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef(null);

  // Global keyboard shortcut: ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (open && e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setResults({ leads: [], contacts: [] });
      setHighlightIndex(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!query.trim()) {
      setResults({ leads: [], contacts: [] });
      return;
    }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [lRes, cRes] = await Promise.all([
          axios.get(`${API}/leads`, { headers, params: { search: query, page_size: 8, page: 1 } }).catch(() => ({ data: [] })),
          axios.get(`${API}/contacts`, { headers, params: { search: query, page_size: 8, page: 1 } }).catch(() => ({ data: [] })),
        ]);
        const lData = Array.isArray(lRes.data) ? lRes.data : (lRes.data?.items || []);
        const cData = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.items || []);
        setResults({ leads: lData.slice(0, 8), contacts: cData.slice(0, 8) });
        setHighlightIndex(0);
      } catch { /* swallow */ } finally {
        setBusy(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, token, open]);

  const navMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NAV_ENTRIES;
    return NAV_ENTRIES.filter(n => n.keys.some(k => k.includes(q)) || n.label.toLowerCase().includes(q));
  }, [query]);

  // Flatten results into a single keyboard-navigable list
  const flatRows = useMemo(() => {
    const rows = [];
    navMatches.slice(0, 4).forEach(n => rows.push({ kind: "nav", ...n }));
    results.leads.forEach(l => rows.push({
      kind: "lead",
      id: l.id,
      label: l.title || `${l.first_name || ""} ${l.last_name || ""}`.trim() || l.company || "Unnamed lead",
      sub: [l.company || l.practice_name, l.email, l.phone, l.stage].filter(Boolean).join(" · "),
    }));
    results.contacts.forEach(c => rows.push({
      kind: "contact",
      id: c.id,
      label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "Unnamed contact",
      sub: [c.company, c.email, c.phone].filter(Boolean).join(" · "),
    }));
    return rows;
  }, [navMatches, results]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, flatRows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = flatRows[highlightIndex];
      if (row) selectRow(row);
    }
  };

  const selectRow = (row) => {
    if (row.kind === "nav") navigate(row.id);
    else if (row.kind === "lead") navigate(`/lead-focus/${row.id}`);
    else if (row.kind === "contact") navigate(`/customers/${row.id}`);
    setOpen(false);
  };

  // Only mount the palette overlay when the user is signed in.
  if (!user) return null;

  return (
    <>
      {open && (
        <div
          data-testid="cmdk-palette"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.40)",
            zIndex: 10001, display: "flex", alignItems: "flex-start",
            justifyContent: "center", paddingTop: "10vh",
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 580, maxWidth: "92vw", maxHeight: "70vh",
              background: "rgba(255,255,255,0.96)",
              borderRadius: 14, overflow: "hidden",
              border: "0.5px solid rgba(0,0,0,0.08)",
              boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
              display: "flex", flexDirection: "column",
            }}>
            <div style={{ padding: "10px 14px", borderBottom: "0.5px solid rgba(0,0,0,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
              <Search style={{ width: 14, height: 14, color: "#6B7280", flexShrink: 0 }} />
              <input
                ref={inputRef}
                data-testid="cmdk-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search leads, contacts, pages…"
                style={{ flex: 1, border: "none", outline: "none", fontSize: 14, fontFamily: "inherit", background: "transparent", padding: 0 }}
              />
              {busy && <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite", color: "#6B7280" }} />}
              <kbd style={{ fontSize: 9, padding: "2px 5px", borderRadius: 4, background: "#F3F4F6", color: "#6B7280", border: "0.5px solid #E5E7EB", fontFamily: "ui-monospace, monospace" }}>ESC</kbd>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 4 }}>
              {flatRows.length === 0 && !busy && (
                <div data-testid="cmdk-empty" style={{ padding: 24, textAlign: "center", color: "#9CA3AF", fontSize: 12.5 }}>
                  {query.trim() ? `No matches for "${query}"` : "Type a name, email, phone, or page…"}
                </div>
              )}
              {flatRows.map((row, i) => {
                const Icon = row.icon;
                const active = i === highlightIndex;
                return (
                  <button
                    key={`${row.kind}-${row.id}`}
                    data-testid={`cmdk-row-${row.kind}-${row.id}`}
                    onMouseEnter={() => setHighlightIndex(i)}
                    onClick={() => selectRow(row)}
                    style={{
                      width: "100%", textAlign: "left",
                      padding: "9px 12px", borderRadius: 8,
                      background: active ? "#EFF6FF" : "transparent",
                      border: "none", cursor: "pointer", fontFamily: "inherit",
                      display: "flex", alignItems: "center", gap: 10,
                    }}>
                    <div style={{ width: 26, height: 26, borderRadius: 6, background: row.kind === "lead" ? "#FFFBEB" : row.kind === "contact" ? "#ECFDF5" : "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {Icon ? <Icon style={{ width: 13, height: 13, color: "#374151" }} /> : row.kind === "lead" ? <Target style={{ width: 13, height: 13, color: "#92400E" }} /> : <Users style={{ width: 13, height: 13, color: "#047857" }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.label}</div>
                      {row.sub && <div style={{ fontSize: 10.5, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1 }}>{row.sub}</div>}
                    </div>
                    <span style={{ fontSize: 9, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, flexShrink: 0 }}>{row.kind}</span>
                    {active && <ArrowRight style={{ width: 11, height: 11, color: "#6B7280", flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
            <div style={{ padding: "6px 12px", borderTop: "0.5px solid rgba(0,0,0,0.08)", fontSize: 9.5, color: "#9CA3AF", display: "flex", justifyContent: "space-between" }}>
              <span><kbd style={kbdStyle}>↑↓</kbd> Navigate · <kbd style={kbdStyle}>↵</kbd> Open</span>
              <span><kbd style={kbdStyle}>⌘K</kbd> Toggle</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const kbdStyle = {
  fontSize: 9, padding: "1px 4px", borderRadius: 3, background: "#F3F4F6",
  border: "0.5px solid #E5E7EB", fontFamily: "ui-monospace, monospace",
  marginRight: 2,
};
